import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from '../../axiosConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCreditCard, 
  faMoneyBillWave, 
  faCalendarAlt, 
  faUser, 
  faClock, 
  faCheck,
  faInfoCircle,
  faLock
} from '@fortawesome/free-solid-svg-icons';
// import Header from '../../components/Header';
import '../../styles/PaymentResult.css';
import '../../styles/AuthPages.css';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { backgroundColor } = useTheme();

  // 获取从 BookingPage 传递的预约信息
  const { service, technician, selectedTime, userId, calculatedPrice, serviceDuration } = location.state || {};
  const [serviceData, setServiceData] = useState(service || null);
  const [technicianData, setTechnicianData] = useState(technician || null);
  
  const [userInfo, setUserInfo] = useState(null);
  const [amount, setAmount] = useState(calculatedPrice || 0);
  const [currency, setCurrency] = useState('usd');
  const [description, setDescription] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [appointmentData, setAppointmentData] = useState(null);

  // 获取用户信息
  useEffect(() => {
    if (userId) {
      axios
        .get(`/yonghu/detail/${userId}`)
        .then((response) => {
          setUserInfo(response.data.data);
        })
        .catch((error) => {
          console.error("获取用户信息失败:", error);
        });
    }
  }, [userId]);

  useEffect(() => {
    if (serviceData) {
      setAmount(calculatedPrice || 0);
      setDescription(`${t('booking.appointmentDetail')}${serviceData.xiangmumingcheng}`);
    }
  }, [serviceData, calculatedPrice, i18n.language, t]);

  // 根据语言重新获取本地化数据，保证展示非中文
  useEffect(() => {
    const currentLang = i18n.language || 'zh';
    const fetchLocalized = async () => {
      try {
        if (service?.id) {
          const res = await axios.get(`/jianshenxiangmu/detail/${service.id}?lang=${currentLang}`);
          if (res?.data?.data) setServiceData(res.data.data);
        }
        if (technician?.jiaolianbianhao) {
          const resTech = await axios.get(`/jiaolianxinxi/${technician.jiaolianbianhao}/detail?lang=${currentLang}`);
          const detail = Array.isArray(resTech?.data?.data) ? resTech.data.data[0] : resTech?.data?.data;
          if (detail) setTechnicianData(detail);
        }
      } catch (err) {
        console.error('获取本地化支付数据失败', err);
      }
    };
    fetchLocalized();
  }, [i18n.language, service?.id, technician?.jiaolianbianhao]);

  const handleGenerateToken = async () => {
    try {
      const omise = window.Omise;
      omise.setPublicKey('pkey_test_61c38rtpcghj68cc4lc');
      console.log("userInfo",userInfo);
      

      return new Promise((resolve, reject) => {
        omise.createToken(
          'card',
          {
            number: cardNumber,
            // name: userInfo?.xingming || 'Card Holder',
            name: /^[a-zA-Z\s]+$/.test(userInfo?.xingming || '') ? userInfo.xingming : 'John Doe',
            expiration_month: expiryMonth,
            expiration_year: expiryYear,
            security_code: cvc,
          },
          (statusCode, response) => {
            if (statusCode === 200 && response.id) {
              resolve(response.id);
            } else {
              reject(new Error(response.message || 'Token generation failed'));
            }
          }
        );
      });
    } catch (error) {
      console.error('Error generating token:', error);
      throw error;
    }
  };

  const selectedDate = new Date(selectedTime);
  const formattedStartTime = selectedDate.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).replace(/\//g, "-");

  // 发送预约确认邮件
  const sendBookingConfirmationEmail = async (bookingData) => {
    try {
      // 检查用户是否有邮箱
      let userEmail = userInfo.youxiang || userInfo.zhanghao;
      if (!userEmail || !userEmail.includes('@')) {
        console.log("用户没有邮箱，跳过邮件发送");
        return;
      }

      const emailData = {
        toEmail: userEmail,
        customerName: userInfo.xingming || userInfo.zhanghao,
        coachName: bookingData.jiaolianxingming,
        serviceName: (serviceData || service)?.xiangmumingcheng,
        bookingTime: bookingData.startTime,
        paymentMethod: "在线支付",
        amount: bookingData.price
      };

      const response = await axios.post('/booking-notification/send-confirmation', emailData);
      
      if (response.data.code === 0) {
        console.log("预约确认邮件发送成功");
      } else {
        console.warn("预约确认邮件发送失败：" + response.data.msg);
      }
    } catch (error) {
      console.error("发送预约确认邮件异常：", error);
    }
  };

  // 支付成功后创建预约
  const handleCreateBooking = async () => {
    try {
      const bookingData = {
        jiaolianbianhao: technician.jiaolianbianhao,
        jiaolianxingming: technician.jiaolianxingming,
        yuyueshuoming: `预约 ${service.xiangmumingcheng}`,
        zhanghao: userInfo.zhanghao,
        xingming: userInfo.xingming,
        shouji: userInfo.shouji,
        sfsh: "是",
        shhf: "",
        startTime: formattedStartTime,
        projectId: service.id,
        xiangmufenlei: service.xiangmuleixing, // 项目类型
        xiangmumingcheng: service.xiangmumingcheng, // 项目名称
        duration: Number(serviceDuration),
        price: calculatedPrice,
        paymentStatus: 1, // 已支付
        paymentMethod: 1, // 在线支付
        status: "pending", // 完成状态
      };

      const response = await axios.post("jiaolianyuyue/addWithoutApprove", bookingData);

      if (response.status === 200 && response.data.code === 0) {
        // 预约创建成功后发送邮件通知
        await sendBookingConfirmationEmail(bookingData);
        
        // 存储预约数据，用于在支付成功页面显示
        const createdAppointment = {
          ...bookingData,
          id: response.data.data || response.data.id || Date.now(),
          startTime: formattedStartTime
        };
        setAppointmentData(createdAppointment);
        navigate("/paymentdone", { state: { appointmentData: createdAppointment } });
      } else {
        navigate("/paymentfail", { 
          state: { errorMessage: "预约创建失败，请联系客服" } 
        });
      }
    } catch (error) {
      console.error("预约创建失败:", error);
      navigate("/paymentfail", { 
        state: { errorMessage: "系统错误，请稍后重试" } 
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cardNumber || !expiryMonth || !expiryYear || !cvc) {
      setMessage(t('payment.selectPaymentMethod'));
      return;
    }
    
    setLoading(true);
    setMessage(t('payment.processing'));

    try {
      // 生成卡片 Token
      const cardToken = await handleGenerateToken();

      // 提交支付请求
      const response = await axios.post(
        '/payment/create',
        null,
        {
          params: {
            amount: Math.round(amount * 100), // Omise 以最小单位为金额
            currency,
            description,
            cardToken,
          },
        }
      );

      if (response.status === 200 && response.data.code === 0) {
        setMessage(t('payment.paymentSuccess'));
        await handleCreateBooking();
      } else {
        setMessage(t('payment.paymentFailed') + '：' + (response.data.msg || ''));
        navigate("/paymentfail", { 
          state: { errorMessage: response.data.msg || t('payment.paymentProcessingMsg') } 
        });
      }
    } catch (error) {
      console.error('Error submitting payment:', error);
      setMessage(t('payment.paymentFailed') + '：' + error.message);
      navigate("/paymentfail", { 
        state: { errorMessage: error.message || t('payment.paymentProcessingMsg') } 
      });
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate(-1);
  };

  // 格式化日期函数
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="home-page">
      {/* <Header /> */}
      <div className="payment-result-container">
        <div className="payment-result-header">
          <h1>
            <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '10px' }} />
            {t('payment.title')}
          </h1>
          <p className="payment-result-message">
            {t('payment.confirmPayment')}
          </p>
        </div>
        
        <div className="payment-details">
          <h2>{t('booking.appointmentDetail')}</h2>
          {serviceData && technicianData ? (
            <>
              <div className="payment-detail-item">
                <span className="detail-label">
                  <FontAwesomeIcon icon={faUser} /> {t('booking.coach')}
                </span>
                <span className="detail-value">{technicianData?.jiaolianxingming || technicianData?.jiaolianbianhao}</span>
              </div>
              <div className="payment-detail-item">
                <span className="detail-label">
                  <FontAwesomeIcon icon={faCalendarAlt} /> {t('booking.serviceInfo')}
                </span>
                <span className="detail-value">{serviceData?.xiangmumingcheng}</span>
              </div>
              <div className="payment-detail-item">
                <span className="detail-label">
                  <FontAwesomeIcon icon={faClock} /> {t('booking.time')}
                </span>
                <span className="detail-value">{formatDate(selectedTime)}</span>
              </div>
              <div className="payment-detail-item">
                <span className="detail-label">
                  <FontAwesomeIcon icon={faMoneyBillWave} /> {t('payment.paymentAmount')}
                </span>
                <span className="detail-value" style={{ color: backgroundColor || '#ff5e57', fontWeight: 'bold' }}>
                  {calculatedPrice}THB
                </span>
              </div>
            </>
          ) : (
            <p>正在加载预约信息...</p>
          )}
        </div>
        
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="description">
              <FontAwesomeIcon icon={faInfoCircle} /> {t('payment.paymentResult')}
            </label>
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('payment.paymentTips')}
              required
            />
          </div>
          
          <h3 style={{ margin: '20px 0 10px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
            <FontAwesomeIcon icon={faLock} style={{ marginRight: '10px' }} />
            {t('payment.paymentMethod')}
          </h3>
          
          <div className="form-group">
            <label htmlFor="cardNumber">{t('payment.paymentMethod')}</label>
            <input
              id="cardNumber"
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/\s/g, ''))}
              placeholder="Card Number"
              maxLength="16"
              required
            />
          </div>
          
          <div style={{ display: 'flex', gap: '15px' }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label>{t('payment.payTime')}</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="MM"
                  value={expiryMonth}
                  onChange={(e) => setExpiryMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  maxLength="2"
                  required
                  style={{ flex: 1 }}
                />
                <span style={{ alignSelf: 'center' }}>/</span>
                <input
                  type="text"
                  placeholder="YY"
                  value={expiryYear}
                  onChange={(e) => setExpiryYear(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  maxLength="2"
                  required
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cvc">CVC/CVV</label>
              <input
                id="cvc"
                type="text"
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 3))}
                placeholder="123"
                maxLength="3"
                required
              />
            </div>
          </div>
          
          {message && (
            <div style={{ 
              padding: '10px', 
              margin: '10px 0', 
              backgroundColor: message.includes('成功') ? '#dff0d8' : '#f2dede',
              border: `1px solid ${message.includes('成功') ? '#d6e9c6' : '#ebccd1'}`,
              borderRadius: '4px',
              color: message.includes('成功') ? '#3c763d' : '#a94442'
            }}>
              {message.includes('成功') ? <FontAwesomeIcon icon={faCheck} /> : null} {message}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
            <button 
              type="button" 
              className="payment-btn btn-secondary" 
              onClick={goBack}
              disabled={loading}
            >
              {t('payment.backBtn')}
            </button>
            <button 
              type="submit" 
              className="payment-btn btn-primary" 
              // style={{ flex: 2 }}
              disabled={loading}
            >
              {loading ? t('payment.processing') : t('payment.confirmPaymentBtn')}
            </button>
          </div>
        </form>
        
        <div className="payment-tips">
          <p><FontAwesomeIcon icon={faLock} style={{ marginRight: '5px' }} /> {t('payment.sslEncryption')}</p>
          <p>{t('payment.testCardInfo')}</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;

